/**
 * Each property corresponds to the actual href. In order to change the URL of
 * a view, edit it here.
 */
export enum Routes {
  NOT_FOUND = "/404",
  LOGIN = "/login",
  LOGIN_LOCAL = "/dev",
  DASHBOARD = "/dashboard",
  DASHBOARD_ORION = "/dashboard-orion",
  HOME = "/",
  USER_PROFILE = "/profile",
  MANAGE_USERS = "/manage-users",
  MANAGE_PUBLISHER_GROUPS = "/manage-publisher-groups",
  MANAGE_CAMPAIGNS = "/manage-campaigns",
  MANAGE_DEALS = "/manage-deals",
  PUBLISHER_MANAGER = "/publisher-manager",
  PUBLISHER_MANAGER_ALLOW_LIST = "/publisher-manager/allow-lists",
  PUBLISHER_MANAGER_OPTIMIZATION_TEMPLATES = "/publisher-manager/templates",
  MANAGE_DEAL_GROUPS = "/manage-deals/deal-groups",
  TRACKING_PIXELS = "/tracking-pixels",
  CREATIVE_LIBRARY = "/creative-library",
  PACING = "/pacing",
  MANAGE_ADVERTISERS = "/manage-advertisers",
  MANAGE_AGENCIES = "/manage-agencies",
  MANAGE_STATIONS = "/manage-stations",
  MANAGE_PRODUCTS = "/manage-products",
  DELIVERY_REPORT = "/delivery-report",
  ADBOOK_ERRORS = "/adbook-errors",
  MANAGE_AUDIENCES = "/audiences",
  AUDIENCE_DATA_PROVISIONING = "/audience-data-provisioning",
  CUSTOM_REPORTING = "/custom-reporting",
  CRYPTO_DASHBOARD = "/crypto-dashboard",
  OOH_PLANNING = "/ooh-planner",
  DATASTUDIO_REPORTS = "/datastudio-reports",
  MANAGE_PUBLISHERS = "/manage-publishers",
  WIDE_ORBIT_ERRORS = "/wide-orbit-errors",
  PLANNER = "/planner",
  ADVANCED_EXPORTS = "/advanced-exports",
  FOOT_TRAFFIC_ATTRIBUTION = "/foot-traffic-attribution",
  FIRST_PARTY_DATA_ONBOARDING = "/first-party-data-onboarding",
  IAS_KEYWORDS_UPLOADER = "/ias-keywords-uploader",
  ADMIN = "/admin",
  KIT = "/kit",
  FIVE_MIN_SEPARATION = "/five-min-separation",
  CONVERSION_DASHBOARD = "/conversion-dashboard",
  SPEND_DASHBOARD = "/spend-dashboard",
  BI_CONVERSION_DASHBOARD = "/bi-conversion-dashboard",
  REPORT_BUILDER = "/report-builder",
  REPORT_CENTER = "/report-center",
  PACING_BETA = "/pacing-beta",
  PLANNER_ADMIN_TOOL = "/planner-admin-tool",
  INSIGHTS_DASHBOARD = "/insights",
  OVERVIEW_DASHBOARD = "/overview",
  OVERVIEW_DASHBOARD_EXTERNAL = "/overview/external",
  DMP_DASHBOARD = "/dmp-dashboard",
  MAVERICK_AI_SEGMENTS = "/maverick-ai-segments-internal",
  MAVERICK_AI_SEGMENTS_EXTERNAL = "/maverick-ai-segments",
  AI_MODE = "/ai",
  MANAGE_AI_AGENTS = "/manage-ai-agents",
  MANAGE_EXPERIMENTS = "/experiments",
  MANAGE_EXPERIMENT_META = "/experiment-meta",
  MANAGE_PRICES = "/manage-prices",
  MAVERICK_SELLER = "/seller",
  MAVERICK_SELLER_PROPOSALS = "/seller/proposals",
  MAVERICK_SELLER_PROPOSAL_NEW = "/seller/proposals/new",
  MAVERICK_SELLER_PROPOSAL_BUILDER = "/seller/proposals/new/builder",
  MAVERICK_SELLER_PROPOSAL_SNAPSHOT = "/seller/proposals/new/snapshot",
  MAVERICK_SELLER_PROPOSAL_DETAIL = "/seller/proposals/:proposalId",
  MAVERICK_SELLER_CLIENTS = "/seller/clients",
  MAVERICK_SELLER_CLIENT_NEW = "/seller/clients/new",
  MAVERICK_AI_CREATIVE = "/seller/maverick-creative",
  MAVERICK_AI_CREATIVE_LIBRARY = "/seller/maverick-creative-library",
  PROMOTED_DASHBOARDS = "/promoted-dashboards"
}

/** A client's discovery page, identified by its client id. */
export function buildClientDiscoveryPath(clientId: string): string {
  return `${Routes.MAVERICK_SELLER_CLIENTS}/${clientId}`;
}

// PK: These are internal views, meaning no client has access to them
// This is where we'll keep a list of internal views that will need to be feature flagged via remote config
// TODO: Programmatically pull internal views from remote config once we get all views on there (if toggled false for everyone, it's unreleased). Is there a way to distinguish feature vs. view in remote config?
export const INTERNAL_VIEWS = [
  { name: "Audiences", route: Routes.MANAGE_AUDIENCES },
  {
    name: "Audience Data Provisioning",
    route: Routes.AUDIENCE_DATA_PROVISIONING
  },
  { name: "Deals", route: Routes.MANAGE_DEALS },
  { name: "Custom Reporting", route: Routes.CUSTOM_REPORTING },
  { name: "Crypto Dashboard", route: Routes.CRYPTO_DASHBOARD },
  {
    name: "DataStudio Reports",
    route: Routes.DATASTUDIO_REPORTS
  },
  {
    name: "Advanced Exports",
    route: Routes.ADVANCED_EXPORTS
  },
  {
    name: "Foot Traffic Attribution",
    route: Routes.FOOT_TRAFFIC_ATTRIBUTION
  },
  { name: "5 Minute Separation", route: Routes.FIVE_MIN_SEPARATION },
  { name: "Conversion Dashboard (BETA)", route: Routes.CONVERSION_DASHBOARD },
  { name: "Spend Dashboard (BETA)", route: Routes.SPEND_DASHBOARD },
  // BI Conversion Dashboard is the new view that embeds the
  // Looker BI Tool Conversion Dashboard in our Madhive UI.
  // We use the "BI" for "Business Intelligence" prefix
  // to differentiate it from the legacy Conversion Dashboard (BETA)
  // views whose route is shown above.
  {
    name: "BI Conversion Dashboard (ALPHA)",
    route: Routes.BI_CONVERSION_DASHBOARD
  },
  { name: "Report Builder (BETA)", route: Routes.REPORT_BUILDER },
  { name: "Report Center (BETA)", route: Routes.REPORT_CENTER },
  {
    name: "Pacing (BETA)",
    route: Routes.PACING_BETA
  },
  {
    name: "Planner Admin Tool",
    route: Routes.PLANNER_ADMIN_TOOL
  },
  {
    name: "DMP Dashboard",
    route: Routes.DMP_DASHBOARD
  },
  {
    name: "Insights Dashboard",
    route: Routes.INSIGHTS_DASHBOARD
  },
  {
    name: "Maverick AI Segments",
    route: Routes.MAVERICK_AI_SEGMENTS
  },
  {
    name: "Manage AI Sessions",
    route: Routes.MANAGE_AI_AGENTS
  },
  {
    name: "Experiments",
    route: Routes.MANAGE_EXPERIMENTS
  },
  {
    name: "Experiment Meta",
    route: Routes.MANAGE_EXPERIMENT_META
  },
  {
    // INTERNAL_VIEWS renders as a flat list with no category heading, so
    // this name should match what the page/main-drawer link actually say
    // ("Proposals"), not the section heading used in the main drawer.
    name: "Proposals",
    route: Routes.MAVERICK_SELLER_PROPOSALS
  },
  {
    name: "AI Creative",
    route: Routes.MAVERICK_AI_CREATIVE
  },
  {
    name: "AI Creative Library",
    route: Routes.MAVERICK_AI_CREATIVE_LIBRARY
  },
  {
    name: "Promoted Dashboards",
    route: Routes.PROMOTED_DASHBOARDS
  }
];

export enum AudienceDataProvisioningRoute {
  MAIN_SCREEN = "/",
  AUDIENCE_DATA_PROVISIONING_INDIVIDUAL_VIEW = "/:organizationId"
}

export enum CreativeLibraryRoute {
  ADD_CREATIVE = "create",
  EXISTING_CREATIVES = "saved-creatives",
  INDIVIDUAL_VIEW = "creatives/:creativeId",
  ADD_BULK_CREATIVE = "bulk-create"
}

export enum ManageCampaignsRoute {
  MAIN_SCREEN = "/",
  CREATE_CAMPAIGN = "campaigns/create",
  CAMPAIGN_INDIVIDUAL_VIEW = "campaigns/:campaignId",
  LINE_ITEM_INDIVIDUAL_VIEW = "lineItems/:lineItemId",
  CREATE_LINE_ITEM = "lineItems/create/:campaignId",
  CREATE_FLIGHT = "flights/create/:lineItemId",
  FLIGHT_INDIVIDUAL_VIEW = "flights/:flightId",
  UPLOAD_CAMPAIGN = "campaigns/upload",
  CREATE_OMNICHANNEL_CAMPAIGN = "campaigns/create-omnichannel"
}

export enum ManageProductsRoute {
  MAIN_SCREEN = "/",
  CREATE_PRODUCT = "products/create",
  PRODUCT_INDIVIDUAL_VIEW = "products/:productId"
}
export enum PublisherManagerRoute {
  MAIN_SCREEN = "/",
  PUBLISHER_EXISTING_SCREEN = "publisher/:id",
  CREATE_PUBLISHER_ALLOW_LIST = "allow-lists/create",
  PUBLISHER_ALLOW_LIST_EXISTING_VIEW = "allow-list/:id",
  CREATE_OPTIMIZATION_TEMPLATE = "templates/create",
  OPTIMIZATION_TEMPLATE_EXISTING_VIEW = "template/:id"
}

export enum PricingRoute {
  MAIN_SCREEN = "/",
  PRODUCT_VIEW = "product/:id",
  STATION_VIEW = "station/:id",
  AGENCY_VIEW = "agency/:id",
  ADVERTISER_VIEW = "advertiser/:id"
}

export enum ManageDealsRoute {
  MAIN_SCREEN = "/",
  CREATE_SCREEN = "deal/create",
  DEAL_GROUPS_CREATE_SCREEN = "deal/deal-groups/create",
  DEAL_GROUPS_EXISTING_SCREEN = "deal-groups/:id",
  BULK_UPLOAD_SCREEN = "deal/upload",
  EXISTING_SCREEN = "deal/:id"
}

export enum LinearMeasurementRoute {
  MAIN_SCREEN = "/",
  REPORT_INDIVIDUAL_VIEW = "reports/:reportId",
  OTTREPORT_INDIVIDUAL_VIEW = "ottreports/:reportId"
}

export enum PremionPacingRoute {
  MAIN_SCREEN = "/"
}

export enum ManageAgenciesRoute {
  MAIN_SCREEN = "/"
}

export enum ManageAudiencesRoute {
  MAIN_SCREEN = "/",
  CREATE_AUDIENCE = "/audience/create",
  AUDIENCE_DETAIL_VIEW = "/audience/:audienceId",
  CREATE_RETARGETING_SEGMENT = "/retargeting-segment/create",
  RETARGETING_SEGMENT_DETAIL_VIEW = "/retargeting-segment/:retargetingSegmentId",
  SEGMENT_DETAIL_VIEW = "/segment/:segmentId"
}

export enum CustomReportingRoute {
  MAIN_SCREEN = "/",
  CREATE_REPORT = "report/create",
  REPORT_DETAIL_VIEW = "report/:reportId"
}

export enum OOHPlanningRoute {
  MAIN_SCREEN = "/",
  CREATE_PLAN = "plan/create",
  PLAN_INDIVIDUAL_VIEW = "plan/:planId",
  PLAN_SCENARIO_VIEW = "plan/:planId/scenario/:scenarioId"
}

export enum CrossPlatformPlannerRoute {
  MAIN_SCREEN = "/",
  CREATE_PLAN = "plan/create",
  PLAN_DETAIL_VIEW = "plan/:planId/*"
}

export enum DataStudioReportsRoute {
  MAIN_SCREEN = "/"
}

export enum ManageUsersRoute {
  MAIN_SCREEN = "/",
  CREATE_USER = "user/create",
  INDIVIDUAL_VIEW = "user/:userId"
}

export enum ManagePublisherGroupRoute {
  MAIN_SCREEN = "/",
  GROUPS_ADD = "add",
  GROUPS_VIEW = "view/:publisherGroupId",
  GROUPS_EDIT = "edit/:publisherGroupId"
}

export enum PublisherGroupsRoute {
  MAIN_SCREEN = "/",
  CREATE = "create",
  EXISTING = ":publisherGroupId"
}

export enum PlannerRoute {
  MAIN = "/",
  CREATION = "create",
  PLAN = "plan/:planId",
  ADD_SCENARIOS = "plan/:planId/add-scenarios",
  SCENARIO = "plan/:planId/scenario/:scenarioId"
}

export enum AdvancedExportsRoute {
  MAIN = "/",
  TEMPLATES_CREATE = "templates/create",
  TEMPLATES_EDIT = "templates/edit/:templateId",
  EXPORT = "export",
  EXPORT_FROM_TEMPLATE = "templates/export/:templateId",
  SCHEDULE_FROM_TEMPLATE = "templates/schedule/:templateId",
  SCHEDULE_EDIT = "templates/schedule/edit/:scheduleId"
}

export enum FootTrafficAttributionRoute {
  MAIN_SCREEN = "/",
  FOOT_TRAFFIC_REPORT_CREATE = "report/create",
  FOOT_TRAFFIC_REPORT_VIEW = "report/:reportId"
}

export enum AdminRoutes {
  HOME = "/",
  ORG_EDITOR = "organizations",
  UX_CONFIG = "ux"
}

export enum TrackingPixelRoutes {
  MAIN_SCREEN = "/",
  TRACKING_PIXEL_VIEW = "pixels/:pixelId"
}

export enum ExperimentsRoute {
  MAIN = "/",
  CREATE = "create",
  DETAIL = ":experimentId"
}

export enum MaverickAISegmentsRoute {
  MAIN_SCREEN = "/",
  CREATE = "create",
  DETAIL = ":personaId"
}
